import type { Context } from '@fortune-sheet/core';
import { getCellTextInfo } from '@fortune-sheet/core';
import type { FortuneCell, FortuneSheet } from './types';

// Excel sizes a row to its wrapped text when the file stores no height for that row; Fortune only
// grows a row when its cell is edited. These heights are applied on open as display state and kept
// out of what is saved or downloaded (see getFullSheets), so Excel goes on fitting the row itself.

type Op = { op?: string; id?: unknown; value?: unknown };
type RowColOp = { type?: string; index?: number; count?: number; direction?: string; start?: number; end?: number };
type SheetConfig = NonNullable<FortuneSheet['config']>;

/** Returns the text height Fortune would give the cell at this width, or null when it cannot measure. */
export type MeasureTextHeight = (cell: FortuneCell, r: number, c: number, cellWidth: number) => number | null;

export interface RowAutofitDefaults {
    rowHeight: number;
    colWidth: number;
}

/** A cell Excel fits its row to: wrapped text that is not part of a merge. */
export const isWrappedTextCell = (cell: FortuneCell | null | undefined): cell is FortuneCell => {
    if (!cell || String(cell.tb) !== '2' || cell.mc) return false;
    if (cell.ct?.t === 'inlineStr') return !!cell.ct.s?.length;
    return typeof cell.v === 'string' && cell.v !== '';
};

/** The row's height: the tallest wrapped text plus Fortune's 2 px (its own on-edit rule), never below the default. */
export const fitRowHeight = (
    row: (FortuneCell | null)[] | null | undefined,
    r: number,
    config: SheetConfig,
    defaults: RowAutofitDefaults,
    measure: MeasureTextHeight,
): number => {
    let height = defaults.rowHeight;
    if (!row) return height;
    for (let c = 0; c < row.length; c++) {
        const cell = row[c];
        if (!isWrappedTextCell(cell) || config.colhidden?.[c] != null) continue;
        const text = measure(cell, r, c, config.columnlen?.[c] ?? defaults.colWidth);
        if (text != null && text + 2 > height) height = text + 2;
    }
    return height;
};

/** Measures one row per step, skipping rows with a stored height and hidden rows; `takeHeights()` hands
 *  over the rows measured since the last call that grow past the default. */
export const createRowAutofitJob = (
    data: (FortuneCell | null)[][],
    config: SheetConfig,
    defaults: RowAutofitDefaults,
    measure: MeasureTextHeight,
) => {
    let heights: Record<number, number> = {};
    let r = 0;
    const step = (): boolean => {
        if (r >= data.length) return true;
        const row = data[r];
        if (config.rowlen?.[r] == null && config.rowhidden?.[r] == null && row?.some(isWrappedTextCell)) {
            const height = fitRowHeight(row, r, config, defaults, measure);
            if (height > defaults.rowHeight) heights[r] = height;
        }
        r += 1;
        return r >= data.length;
    };
    const takeHeights = () => {
        const taken = heights;
        heights = {};
        return taken;
    };
    return { step, takeHeights };
};

/** Recipe body: adds `rows` to the sheet's row heights, skipping rows that have one, as one new frozen
 *  object (one write, not one per row; a whole statement can be 30k rows). Returns the rows it set. */
export const addRowHeights = (ctx: Context, sheetIndex: number, rows: Record<number, number>): Record<number, number> => {
    const file = ctx.luckysheetfile[sheetIndex];
    const config = file.config ?? {};
    const before = config.rowlen ?? {};
    const merged: Record<string, number> = {};
    Object.keys(before).forEach((key) => {
        merged[key] = before[key];
    });
    const added: Record<number, number> = {};
    Object.keys(rows).forEach((key) => {
        if (merged[key] != null) return;
        merged[key] = rows[Number(key)];
        added[Number(key)] = rows[Number(key)];
    });
    if (!Object.keys(added).length) return added;
    config.rowlen = Object.freeze(merged);
    file.config = config;
    if (ctx.currentSheetId === file.id) ctx.config = config;
    return added;
};

/** Recipe body, after `addRowHeights` on the tab on screen: rows above the viewport that just grew would
 *  push what the user is reading down the screen, so the view scrolls by the same amount. The caller
 *  recomputes the row offsets in the same update when this returns true, so the next paint is consistent. */
export const keepRowsOnScreen = (ctx: Context, added: Record<number, number>): boolean => {
    const offsets = ctx.visibledatarow;
    const top = ctx.scrollTop ?? 0;
    if (!offsets?.length || top <= 0) return false;
    // the first row whose bottom edge is below the top of the viewport
    let lo = 0;
    let hi = offsets.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (offsets[mid] > top) hi = mid;
        else lo = mid + 1;
    }
    const hidden = ctx.config?.rowhidden ?? {};
    const zoom = ctx.zoomRatio ?? 1;
    // Fortune's row pitch (calcRowColSize): the height plus a 1 px gridline, at the zoom.
    const before = Math.round((ctx.defaultrowlen + 1) * zoom);
    let grown = 0;
    Object.keys(added).forEach((key) => {
        const r = Number(key);
        if (r < lo && hidden[r] == null) grown += Math.round((added[r] + 1) * zoom) - before;
    });
    if (!grown) return false;
    ctx.scrollTop = Math.max(0, top + grown);
    return true;
};

let measureCanvas: CanvasRenderingContext2D | null | undefined;

/** Fortune's own text layout (getCellTextInfo), at zoom 1 since row heights are stored unzoomed. */
export const createTextHeightMeasure = (ctx: Context): MeasureTextHeight | null => {
    if (measureCanvas === undefined) measureCanvas = document.createElement('canvas').getContext?.('2d') ?? null;
    const canvas = measureCanvas;
    if (!canvas) return null;
    const sheetCtx = Object.create(ctx, { zoomRatio: { value: 1 } }) as Context;
    return (cell, r, c, cellWidth) => {
        const info = getCellTextInfo(cell, canvas, sheetCtx, { r, c, cellWidth });
        return info ? info.textHeightAll : null;
    };
};

/** Drops the heights applied on open from what is saved or downloaded: a row whose height is still the
 *  applied one goes out with no height, as the file came in. Same reference when there is nothing to drop. */
export const withoutAutoRowHeights = (sheet: FortuneSheet, auto: Record<number, number> | undefined): FortuneSheet => {
    const rowlen = sheet.config?.rowlen;
    if (!auto || !rowlen) return sheet;
    const kept: Record<string, number> = {};
    let dropped = false;
    Object.keys(rowlen).forEach((key) => {
        if (auto[Number(key)] === rowlen[key]) dropped = true;
        else kept[key] = rowlen[key];
    });
    return dropped ? { ...sheet, config: { ...sheet.config, rowlen: kept } } : sheet;
};

/** Keeps the applied heights on their rows when rows are inserted or deleted (and on undo/redo, which
 *  replay the inverse op). Rows a delete removes are held so its undo, an insert at the same place,
 *  gets them back. */
export const shiftAutoRowHeights = (
    auto: Map<string, Record<number, number>>,
    held: Map<string, Record<number, number>>,
    ops: Op[],
): void => {
    ops.forEach((op) => {
        const value = (op.value ?? {}) as RowColOp;
        if (value.type !== 'row' || (op.op !== 'insertRowCol' && op.op !== 'deleteRowCol')) return;
        const id = String(op.id);
        const heights = auto.get(id);
        if (op.op === 'insertRowCol') {
            const at = (value.index ?? 0) + (value.direction === 'rightbottom' ? 1 : 0);
            const count = value.count ?? 0;
            const key = `${id}:${at}:${count}`;
            const restored = held.get(key);
            held.delete(key);
            if (!heights && !restored) return;
            const next: Record<number, number> = {};
            Object.entries(heights ?? {}).forEach(([row, px]) => {
                const n = Number(row);
                next[n >= at ? n + count : n] = px;
            });
            Object.entries(restored ?? {}).forEach(([offset, px]) => {
                next[at + Number(offset)] = px;
            });
            auto.set(id, next);
            return;
        }
        if (!heights) return;
        const start = value.start ?? 0;
        const end = value.end ?? start;
        const count = end - start + 1;
        const next: Record<number, number> = {};
        const removed: Record<number, number> = {};
        Object.entries(heights).forEach(([row, px]) => {
            const n = Number(row);
            if (n < start) next[n] = px;
            else if (n > end) next[n - count] = px;
            else removed[n - start] = px;
        });
        held.set(`${id}:${start}:${count}`, removed);
        auto.set(id, next);
    });
};
